import React, { useState, useEffect, createContext, useContext } from "react";
import { useRouter } from "next/router";
import classNames from "classnames";
import NavigatableListView from "../NavigatableListView/NavigatableListView";
import styles from "./NavigatableMenuContainer.module.scss";

// Create context for navigatable list handler
export const NavigatableContext = createContext(null);

export default function NavigatableMenuContainer({ 
    children, 
    menu, 
    header,
    onItemClick,
    listViewClassName 
}) {
    const router = useRouter();
    const [showList, setShowList] = useState(false);
    const [activeNavigatableItem, setActiveNavigatableItem] = useState(null);
    const [shouldRenderList, setShouldRenderList] = useState(false);

    // Check if current URL matches any navigatable item and auto-open list
    useEffect(() => {
        if (!menu) return;
        
        console.log('NavigatableMenuContainer - Checking menu:', menu);
        console.log('NavigatableMenuContainer - Current path:', router.asPath);
        
        // Find if any menu item has a navigatable sub_page that matches current URL
        for (const menuItem of menu) {
            if (menuItem?.sub_pages) {
                console.log('Checking menu item:', menuItem.title, 'sub_pages:', menuItem.sub_pages);
                for (const subPage of menuItem.sub_pages) {
                    console.log('Checking subPage:', subPage.title, 'navigatable:', subPage.navigatable, 'link:', subPage.link);
                    if (subPage.navigatable && router.asPath.startsWith(subPage.link)) {
                        // Only auto-open if we're on a detail page (not the base list page)
                        const normalizedPath = router.asPath.split('?')[0].replace(/\/$/, '');
                        const normalizedBase = subPage.link.replace(/\/$/, '');
                        console.log('Found navigatable match! normalized:', normalizedPath, 'base:', normalizedBase);
                        if (normalizedPath !== normalizedBase) {
                            console.log('Opening list for:', subPage.title);
                            setActiveNavigatableItem(subPage);
                            setShouldRenderList(true);
                            requestAnimationFrame(() => {
                                setShowList(true);
                            });
                            return;
                        }
                    }
                }
            }
        }
        console.log('No navigatable match found, closing list');
        // If we're on the base page, close the list
        setShowList(false);
        setTimeout(() => {
            setShouldRenderList(false);
            setActiveNavigatableItem(null);
        }, 300);
    }, [router.asPath, menu]);

    const handleShowNavigatableList = (subPage) => {
        console.log('handleShowNavigatableList called with:', subPage);
        setActiveNavigatableItem(subPage);
        setShouldRenderList(true);
        requestAnimationFrame(() => {
            setShowList(true);
        });
    };

    const handleBack = () => {
        setShowList(false);
        setTimeout(() => {
            setShouldRenderList(false);
            setActiveNavigatableItem(null);
        }, 300);
    };

    return (
        <NavigatableContext.Provider value={handleShowNavigatableList}>
            <div className={styles.container}>
                <div className={styles.navigationContainer}>
                    <div className={classNames(styles.contentWrapper, { [styles.showList]: showList })}>
                        <div className={styles.mainView}>
                            {header && <div className={styles.headerContainer}>{header}</div>}
                            {children}
                        </div>
                        <div className={classNames(styles.listView, listViewClassName)}>
                            {shouldRenderList && activeNavigatableItem && (
                                <NavigatableListView
                                    useDataHook={activeNavigatableItem.useDataHook}
                                    titleValue={activeNavigatableItem.titleValue}
                                    menuItem={activeNavigatableItem}
                                    onBack={handleBack}
                                    onItemClick={onItemClick}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </NavigatableContext.Provider>
    );
}

// Custom hook to use the navigatable context
export function useNavigatableContext() {
    return useContext(NavigatableContext);
}
